/**
 * A failed void arrives as {status: 2, data: '<CODE>'} -- axios rejects the 400 and processError
 * reads response.data.message into `data`.
 *
 * The backend diagnoses a rejected void by reading the code back from EFS (getMoneyCodeById), so
 * these codes say *why* it was refused rather than just that it failed. VOID_FAILED is the
 * catch-all the backend sends when it could not establish a reason.
 */
export function getVoidErrorKey(code) {
    switch (code) {
        case 'VOID_FAILED_ALREADY_VOIDED':
            return 'message.void_failed_already_voided';
        case 'VOID_FAILED_CODE_USED':
            return 'message.void_failed_code_used';
        case 'VOID_FAILED_CODE_NOT_FOUND':
            return 'message.void_failed_code_not_found';
        default:
            return 'message.void_failed';
    }
}

/**
 * True when the backend reconciled our copy of the row as part of refusing the void, so the list
 * is stale and needs re-reading. Only already-voided does this -- the other refusals change
 * nothing server-side.
 */
export function voidErrorNeedsRefresh(code) {
    return code === 'VOID_FAILED_ALREADY_VOIDED';
}

/**
 * A money code is issued to either a contact or an organization. The create/edit forms show a
 * button-group (IssuedToType) that decides which of the two selects is visible and required.
 */
export const ISSUED_TO_CONTACT = 1;
export const ISSUED_TO_ORGANIZATION = 2;

export function getIssuedToType(item) {
    return (!!item?.OrganizationID && !item?.ContactID) ? ISSUED_TO_ORGANIZATION : ISSUED_TO_CONTACT;
}

/**
 * Shows and requires the select matching `type` and hides the other. Mutates and returns `fields`.
 */
export function applyIssuedToType(fields, type, contactKey, organizationKey) {
    const isContact = type !== ISSUED_TO_ORGANIZATION;
    fields[contactKey].type = isContact ? 'select-search' : 'hidden';
    fields[contactKey].validate = isContact ? ['empty'] : [''];
    fields[organizationKey].type = isContact ? 'hidden' : 'select-search';
    fields[organizationKey].validate = isContact ? [''] : ['empty'];
    return fields;
}

/**
 * Strips IssuedToType from submitted params and removes the recipient that was not picked.
 * With `clearOther` the unpicked key is sent as null instead, so an update clears the stored value.
 */
export function pickIssuedTo(params, contactKey, organizationKey, clearOther = false) {
    const unpickedKey = params.IssuedToType === ISSUED_TO_ORGANIZATION ? contactKey : organizationKey;
    if (clearOther) {
        params[unpickedKey] = null;
    } else {
        delete params[unpickedKey];
    }
    delete params.IssuedToType;
    return params;
}
